import { Chip, styled } from "@mui/material";
import { orange, purple } from "@mui/material/colors";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import Confetti from "react-confetti";

import VersionLog from "../../../../Assets/VersionLog";
import { UserContext } from "../../../../Context/UserContext";
import { Notification } from "../../../../Types/Notification";
import { LocalStorageConfigs } from "../../../../Utils/LocalStorage";
import Modal from "../../../Helpers/Modal";
import StyledReactMarkdown from "../../../Helpers/StyledReactMarkdown";
import useAnnounceNewerVersion from "./Version.UseAnnounceNewerVersion";

const confettiColors: string[] = [
  // shades of orange
  // eslint-disable-next-line prettier/prettier
  "#DF822C",
  orange[500],
  orange[700],
  orange[900],
  // shades of purple
  // eslint-disable-next-line prettier/prettier
  "#611549",
  purple[600],
  purple[800]
];

const confetti = {
  leaf: {
    shape: new Path2D(`M5 445 c-17 -136 38 -241 178 -336 51 -36 57 -43 57 -75 0 -21 4 -33
    10 -29 18 11 11 146 -9 191 -18 40 -30 52 -130 126 -53 40 -78 73 -86 113 l-6
    30 17 -30 c25 -45 63 -83 114 -112 66 -38 100 -72 125 -127 22 -46 24 -47 35
    -26 17 31 7 91 -20 128 -13 17 -59 50 -103 74 -45 24 -101 62 -125 86 -25 23
    -46 42 -48 42 -1 0 -6 -25 -9 -55z`),
    aspectRatio: { x: 0.07, y: 0.07 }
  },
  gem: {
    shape: new Path2D(`M623 1746 c-166 -28 -303 -53 -306 -56 -2 -3 -75 -99 -161 -215 -112
    -149 -156 -216 -153 -231 4 -24 904 -1030 930 -1039 9 -3 25 -3 35 0 9 4 222
    234 472 513 328 365 455 513 455 530 0 26 -298 436 -324 447 -19 8 -609 105
    -630 104 -9 -1 -152 -24 -318 -53z m52 -196 l-119 -119 -60 89 c-32 50 -57 92
    -55 93 5 4 307 54 334 56 13 1 -18 -37 -100 -119z m415 -15 l135 -135 -275 0
    -275 0 135 135 c74 74 137 135 140 135 3 0 66 -61 140 -135z m203 108 c92 -15
    167 -30 167 -33 0 -3 -26 -44 -58 -92 l-58 -87 -119 119 c-66 66 -115 120
    -110 120 6 0 85 -12 178 -27z m-883 -173 c29 -44 51 -82 49 -84 -8 -7 -271
    -58 -276 -53 -6 7 159 227 167 222 4 -3 31 -41 60 -85z m1234 -28 c43 -57 76
    -106 73 -109 -5 -5 -268 45 -276 53 -2 2 22 43 53 90 32 48 61 82 65 78 5 -5
    43 -55 85 -112z m-383 -189 c-10 -27 -83 -227 -162 -445 -79 -219 -146 -398
    -149 -398 -3 0 -70 179 -149 398 -79 218 -152 418 -162 445 l-19 47 330 0 330
    0 -19 -47z m-733 10 c5 -16 65 -179 132 -363 67 -184 129 -353 136 -375 l15
    -40 -35 35 c-66 67 -626 693 -623 697 5 4 326 71 349 72 9 1 20 -11 26 -26z
    m1042 -8 c95 -19 175 -36 177 -38 3 -4 -558 -631 -623 -697 l-35 -35 15 40 c7
    22 69 191 136 375 67 184 127 347 132 363 6 15 13 27 17 27 4 0 86 -16 181
    -35z`),
    aspectRatio: { x: 0.02, y: 0.02 }
  }
};

/** Draw the svg shape for either a leaf or gem to be used as a confetti item. */
function drawConfetti(this: { shape?: keyof typeof confetti; isSet?: boolean }, ctx: CanvasRenderingContext2D): void {
  if (!this.isSet || !this.shape) {
    // shape ratio of leaf to gem is 9:1
    this.shape = Math.random() * 10 < 8 ? "leaf" : "gem";
    this.isSet = true;
  }
  ctx.scale(confetti[this.shape].aspectRatio.x, confetti[this.shape].aspectRatio.y);
  ctx.fill(confetti[this.shape].shape);
}

const Version: React.FC = () => {
  const { setNotifications } = useContext(UserContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [confettiCount, setConfettiCount] = useState(0);
  useAnnounceNewerVersion();
  // on mount, check current and changing version number
  useEffect(() => {
    if (
      !process.env.REACT_APP_VERSION_RELEASE_DATE ||
      !process.env.REACT_APP_VERSION_NO ||
      !process.env.REACT_APP_CONFETTI_DAYS
    )
      throw "Version Props Not Configured.";
    const prevVersion = localStorage.getItem(LocalStorageConfigs.versionNumber.key);
    if (
      DateTime.now().diff(DateTime.fromISO(process.env.REACT_APP_VERSION_RELEASE_DATE), "days").days <
        Number(process.env.REACT_APP_CONFETTI_DAYS) &&
      (!prevVersion || prevVersion < process.env.REACT_APP_VERSION_NO)
    ) {
      setNotifications(n => [
        ...n,
        new Notification(`You are now running version ${process.env.REACT_APP_VERSION_NO}!`, "browser_updated")
      ]);
      setConfettiCount(300);
    }
    localStorage.setItem(LocalStorageConfigs.versionNumber.key, process.env.REACT_APP_VERSION_NO);
  }, []);

  return (
    <>
      <Chip
        label={`${process.env.REACT_APP_VERSION_NO}`}
        size="small"
        color="secondary"
        sx={{ ml: 2 }}
        onClick={() => setModalOpen(true)}
      />
      {Boolean(confettiCount) && (
        <Confetti
          colors={confettiColors}
          numberOfPieces={confettiCount}
          confettiSource={{ x: 260, y: 50, w: 5, h: 5 }}
          onConfettiComplete={() => setConfettiCount(0)}
          recycle={false}
          drawShape={drawConfetti}
        />
      )}
      <Modal maxWidth="md" fullWidth open={modalOpen} handleConfirm={() => setModalOpen(false)} title="Version History">
        <MaxImageSizeStyling>
          <StyledReactMarkdown>{VersionLog}</StyledReactMarkdown>
        </MaxImageSizeStyling>
      </Modal>
    </>
  );
};

/** Limit images to a maximum size. Be sure the source doesn't look stretched. */
const MaxImageSizeStyling = styled("div")(() => ({
  "& img": { maxWidth: "800px", maxHeight: "300px" }
}));

export default Version;
